import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { SaveStatus } from "../../../shared/utility/save-status";
import { KModal } from "../../ui-kit/k-modal/k-modal";
import { KModalFooter } from "../../ui-kit/k-modal/k-modal-footer";
import KuikaButton, {
  type IKuikaButtonStatusLabels,
  type KuikaButtonStatus
} from "../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import KuikaH5 from "../../ui-kit/typography/H5/H5";
import KuikaSmallText from "../../ui-kit/typography/SmallText/SmallText";
import KuikaTextMedium from "../../ui-kit/typography/TextMedium/TextMedium";
import { buildBoardPath } from "./board-routing";
import { BoardService } from "./board-service";
import "./new-board-modal.scss";
import { Board, BoardKind, BoardMode, BoardScope, Project } from "./types";
import { useBoardConfig } from "./use-board";

/** A board is created, not saved. */
const CREATE_STATUS_LABELS: IKuikaButtonStatusLabels = { loading: "Creating", success: "Created" };

interface NewBoardModalProps {
  open: boolean;
  onClose: () => void;
  // When set, the board is pre-scoped to this (already board-enabled) project: the type and project
  // pickers are hidden and only the name + workflow are asked. The project — and its code — are
  // chosen when the project is enabled for boards, never here.
  projectKey?: string;
}

const NewBoardModal: React.FC<NewBoardModalProps> = ({ open, onClose, projectKey }) => {
  const navigate = useNavigate();
  const { domainName } = useParams<{ domainName: string }>();
  // Boards are created over projects already enabled for the board feature — never by linking a new
  // platform project here (that, with its code, is done on the board home / project hub).
  const { config } = useBoardConfig();
  const projects: Project[] = config.projects;
  const locked: boolean = Boolean(projectKey);

  const [kind, setKind] = useState<BoardKind>("project");
  const [mode, setMode] = useState<BoardMode>("scrum");
  const [name, setName] = useState<string>("");
  const [selectedKey, setSelectedKey] = useState<string>("");
  const [multiKeys, setMultiKeys] = useState<string[]>([]);
  // Where Create stands. Owned by `SaveStatus`, never set directly.
  const [createStatus, setCreateStatus] = useState<KuikaButtonStatus>("idle");
  const createMachineRef = useRef<SaveStatus | null>(null);
  if (createMachineRef.current === null) {
    createMachineRef.current = new SaveStatus(setCreateStatus);
  }
  const createMachine: SaveStatus = createMachineRef.current;
  useEffect((): (() => void) => (): void => createMachine.dispose(), [createMachine]);

  useEffect((): void => {
    if (!open) return;
    setKind("project");
    setMode("scrum");
    setName("");
    setSelectedKey(projectKey ?? "");
    setMultiKeys([]);
  }, [open, projectKey]);

  const scopedProject: Project | undefined = locked ? projects.find((p: Project) => p.key === projectKey) : undefined;

  const handleCreate = async (): Promise<void> => {
    // A second press while the board is being made or its ✓ is held would make a second board.
    if (!createMachine.canStart) return;
    createMachine.start();
    try {
      let scope: BoardScope;
      let fallbackName: string;
      if (locked || kind === "project") {
        const key: string = projectKey ?? selectedKey;
        const project: Project | undefined = projects.find((p: Project) => p.key === key);
        if (!project) {
          createMachine.reset();
          return;
        }
        scope = { kind: "project", projectKey: project.key };
        fallbackName = `${project.name} board`;
      } else {
        if (multiKeys.length === 0) {
          createMachine.reset();
          return;
        }
        scope = { kind: "multi_project", projectKeys: multiKeys };
        fallbackName = "Multi-project board";
      }
      const board: Board | null = await BoardService.createBoard(name.trim() || fallbackName, scope, mode);
      if (!board) {
        createMachine.fail();
        return;
      }
      // The ✓ goes up first; the dialog leaves after it and the new board opens.
      createMachine.succeed((): void => {
        onClose();
        navigate(buildBoardPath(domainName ?? "", board));
      });
    } catch {
      createMachine.fail();
    }
  };

  // Cancel, the X and Escape. During the ✓ hold the dialog is already on its way to the new board;
  // a close then would leave the board unopened.
  const handleClose = (): void => {
    if (createMachine.current === "success") return;
    onClose();
  };

  const toggleMultiKey = (key: string): void => {
    setMultiKeys((prev: string[]) =>
      prev.includes(key) ? prev.filter((entry: string) => entry !== key) : [...prev, key]
    );
  };

  const isValid: boolean = locked || (kind === "project" ? !!selectedKey : multiKeys.length > 0);

  return (
    <KModal
      showModal={open}
      closeModal={handleClose}
      width={460}
      header={<KuikaH5>New board</KuikaH5>}
      footer={
        <KModalFooter
          onCancel={handleClose}
          submitLabel="Create board"
          submitTestId="new-board-create"
          onSubmit={handleCreate}
          status={createStatus}
          statusLabels={CREATE_STATUS_LABELS}
          disabled={!isValid}
        />
      }
    >
      <div className="kuika-new-board-modal">
        {scopedProject && (
          <div className="kuika-new-board-modal__field">
            <KuikaTextMedium>Project</KuikaTextMedium>
            <div className="kuika-new-board-modal__scoped">
              <span className="kuika-new-board-modal__scoped-dot" style={{ background: scopedProject.color }} />
              <span className="kuika-new-board-modal__scoped-name" data-testid="new-board-scoped-project">
                {scopedProject.name}
              </span>
            </div>
          </div>
        )}

        {!locked && (
          <div className="kuika-new-board-modal__field">
            <KuikaTextMedium>Type</KuikaTextMedium>
            <div className="kuika-new-board-modal__kind-grid">
              <button
                type="button"
                className={`kuika-new-board-modal__kind${kind === "project" ? " kuika-new-board-modal__kind--active" : ""}`}
                onClick={() => setKind("project")}
              >
                <span className="kuika-new-board-modal__kind-title">Project board</span>
                <KuikaSmallText>One project. Inherits its workflow, fields, and sprints.</KuikaSmallText>
              </button>
              <button
                type="button"
                className={`kuika-new-board-modal__kind${kind === "multi_project" ? " kuika-new-board-modal__kind--active" : ""}`}
                onClick={() => setKind("multi_project")}
              >
                <span className="kuika-new-board-modal__kind-title">Multi-project board</span>
                <KuikaSmallText>Aggregates issues across N projects. Cards show a project chip.</KuikaSmallText>
              </button>
            </div>
          </div>
        )}

        <div className="kuika-new-board-modal__field">
          <KuikaTextMedium>Workflow</KuikaTextMedium>
          <div className="kuika-new-board-modal__kind-grid">
            <button
              type="button"
              data-testid="new-board-mode-scrum"
              className={`kuika-new-board-modal__kind${mode === "scrum" ? " kuika-new-board-modal__kind--active" : ""}`}
              onClick={() => setMode("scrum")}
            >
              <span className="kuika-new-board-modal__kind-title">Scrum</span>
              <KuikaSmallText>Plan work in sprints with a backlog and capacity tracking.</KuikaSmallText>
            </button>
            <button
              type="button"
              data-testid="new-board-mode-kanban"
              className={`kuika-new-board-modal__kind${mode === "kanban" ? " kuika-new-board-modal__kind--active" : ""}`}
              onClick={() => setMode("kanban")}
            >
              <span className="kuika-new-board-modal__kind-title">Kanban</span>
              <KuikaSmallText>Continuous flow. A flat backlog, no sprints.</KuikaSmallText>
            </button>
          </div>
        </div>

        <div className="kuika-new-board-modal__field">
          <KuikaTextMedium>Name</KuikaTextMedium>
          <KuikaInput
            value={name}
            onChange={(_event, value: string | number) => setName(String(value))}
            placeholder={scopedProject ? `e.g. ${scopedProject.name} board` : "e.g. Delivery board"}
            autoFocus
          />
        </div>

        {!locked && projects.length === 0 && (
          <div className="kuika-new-board-modal__field">
            <KuikaSmallText>No board-enabled projects yet. Enable a project from the board home first.</KuikaSmallText>
          </div>
        )}

        {!locked && kind === "project" && projects.length > 0 && (
          <div className="kuika-new-board-modal__field">
            <KuikaTextMedium>Project</KuikaTextMedium>
            <div className="kuika-new-board-modal__project-list">
              {projects.map((project: Project) => (
                <button
                  key={project.key}
                  type="button"
                  className={`kuika-new-board-modal__project-row${
                    selectedKey === project.key ? " kuika-new-board-modal__project-row--active" : ""
                  }`}
                  onClick={() => setSelectedKey(project.key)}
                >
                  <span className="kuika-new-board-modal__scoped-dot" style={{ background: project.color }} />
                  <span className="kuika-new-board-modal__project-name">{project.name}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {!locked && kind === "multi_project" && projects.length > 0 && (
          <div className="kuika-new-board-modal__field">
            <KuikaTextMedium>Projects ({multiKeys.length} selected)</KuikaTextMedium>
            <div className="kuika-new-board-modal__project-list">
              {projects.map((project: Project) => {
                const selected: boolean = multiKeys.includes(project.key);
                return (
                  <button
                    key={project.key}
                    type="button"
                    className={`kuika-new-board-modal__project-row${selected ? " kuika-new-board-modal__project-row--active" : ""}`}
                    onClick={() => toggleMultiKey(project.key)}
                  >
                    <span className="kuika-new-board-modal__check" data-checked={selected ? "true" : "false"}>
                      {selected ? "✓" : ""}
                    </span>
                    <span className="kuika-new-board-modal__scoped-dot" style={{ background: project.color }} />
                    <span className="kuika-new-board-modal__project-name">{project.name}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </KModal>
  );
};

export default NewBoardModal;
